import { type CSSProperties, type ReactNode, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft } from "lucide-react";

import { formatCurrency } from "../../utils/currency.ts";
import { getProjectPhases, type ProjectPhaseView } from "./project-utilisation-overview.tsx";
import type { ProjectPhaseDailyActivity, ProjectTrackingReport } from "./types.ts";
import styles from "./workstream-tracking-page.module.css";

type Props = {
  project: ProjectTrackingReport;
  currency: string;
  projectStart: string | null;
  projectEnd: string | null;
  title: string;
  onBack: () => void;
};

type TimelinePhase = ProjectPhaseView & {
  color: string;
  commercial: number;
  internal: number;
  dailyActivity: ProjectPhaseDailyActivity[];
};

type TimelineWeek = {
  key: string;
  start: Date;
  end: Date;
  days: number;
  label: string;
};
type TimelineRowParts = { key: string; phase: ReactNode; lane: ReactNode; percent: ReactNode };
type TimelineTooltip = { content: ReactNode; style: CSSProperties };

const day = 86_400_000;
const phaseColors = ["#5c59df", "#793def", "#cf7900", "#35a255", "#4169ec", "#743ce8"];
const number = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });
const parseDate = (value?: string | null) => value ? new Date(`${value.slice(0, 10)}T00:00:00`) : null;
const formatDate = (value: Date) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(value);
const formatLongDate = (value: Date) => new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", year: "numeric" }).format(value);
const monthLabel = (value: Date) => new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit" }).format(value).toUpperCase();
const hours = (value: number) => `${number.format(value)}h`;
const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));
const addDays = (value: Date, count: number) => new Date(value.getTime() + count * day);
const dateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const date = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${date}`;
};

export function InlineProjectPhaseTimeline({ project, currency, projectStart, projectEnd, title, onBack }: Props) {
  const cardRef = useRef<HTMLElement>(null);
  const [tooltip, setTooltip] = useState<TimelineTooltip | null>(null);
  const start = parseDate(projectStart);
  const end = parseDate(projectEnd);
  const validRange = Boolean(start && end && end >= start);
  const phases = useMemo<TimelinePhase[]>(() => {
    const actualTotal = Number(project.actualHours ?? 0);
    return getProjectPhases(project).map((phase, index) => {
      const contributorRows = (project.phaseContributors ?? []).filter((item) => String(item.phaseId) === phase.key || item.phaseName === phase.name);
      const proportionalShare = actualTotal > 0 ? Number(phase.utilised ?? 0) / actualTotal : 0;
      const contributorCommercial = contributorRows.reduce((sum, item) => sum + Number(item.commercialBurn ?? 0), 0);
      const contributorInternal = contributorRows.reduce((sum, item) => sum + Number(item.internalCost ?? 0), 0);
      return {
        ...phase,
        color: phaseColors[index % phaseColors.length],
        commercial: phase.commercial ?? (contributorRows.length ? contributorCommercial : Number(project.commercialBurn ?? 0) * proportionalShare),
        internal: phase.internal ?? (contributorRows.length ? contributorInternal : Number(project.internalCost ?? 0) * proportionalShare),
        dailyActivity: phase.dailyActivity ?? [],
      };
    });
  }, [project]);

  if (!validRange) return <section className={styles.inlineTimelineCard}>
    <TimelineHeader onBack={onBack} title={title} />
    <div className={styles.inlineTimelineUnavailable}>Start and end dates must be configured to display this timeline.</div>
  </section>;

  const totalDays = Math.floor((end!.getTime() - start!.getTime()) / day) + 1;
  const weekSegments = buildWeeks(start!, end!);
  const weeks = weekSegments.length;
  const months = buildMonths(start!, end!);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayVisible = today >= start! && today <= end!;
  const todayLeft = todayVisible ? clamp(((today.getTime() - start!.getTime()) / day / totalDays) * 100, 0, 100) : 0;
  const laneStyle = { "--today-left": `${todayLeft}%` } as CSSProperties;
  const tableStyle = { "--timeline-lane-width": `${Math.max(weekSegments.length * 64, 768)}px` } as CSSProperties;
  const showTooltip = (content: ReactNode, anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    const chart = cardRef.current?.getBoundingClientRect();
    const width = Math.min(460, window.innerWidth - 24, (chart?.width ?? window.innerWidth) - 24);
    const minimumLeft = Math.max(12, chart?.left ?? 12);
    const maximumLeft = Math.max(minimumLeft, Math.min(window.innerWidth - width - 12, (chart?.right ?? window.innerWidth) - width - 12));
    const left = clamp(rect.left + rect.width / 2 - width / 2, minimumLeft, maximumLeft);
    const estimatedHeight = Math.min(350, window.innerHeight - 24);
    const top = rect.bottom + estimatedHeight + 12 <= window.innerHeight
      ? rect.bottom + 9
      : clamp(rect.top - estimatedHeight - 9, 12, window.innerHeight - estimatedHeight - 12);
    setTooltip({ content, style: { width, left, top } });
  };
  const rows: TimelineRowParts[] = phases.map((phase) => buildTimelineRowParts({
    phase, projectStart: start!, projectEnd: end!, totalDays, weeks: weekSegments,
    currency, todayVisible, laneStyle, onShowTooltip: showTooltip, onHideTooltip: () => setTooltip(null),
  }));

  return <section className={styles.inlineTimelineCard} ref={cardRef}>
    <TimelineHeader onBack={onBack} title={title} detail={`${formatLongDate(start!)} – ${formatLongDate(end!)} · ${weeks} weeks`} />
    <div className={styles.inlineTimelineGrid}>
      <div className={styles.inlineTimelineFixedLeft}>
        <strong className={styles.inlineTimelineFixedHeader}>Phase</strong>
        {rows.map((row) => <div className={styles.inlineTimelineFixedRow} key={row.key}>{row.phase}</div>)}
      </div>
      <div className={styles.inlineTimelineScroller} onScroll={() => setTooltip(null)}>
        <div className={styles.inlineTimelineMiddle} style={tableStyle}>
          <div className={styles.inlineTimelineMonths} style={laneStyle}>
            <div className={styles.inlineTimelineMonthLabels}>{months.map((month) => <span key={month.key} style={{ flex: month.days }}>{month.label}</span>)}</div>
            <div className={styles.inlineTimelineWeekLabels}>{weekSegments.map((week) => <span key={week.key} style={{ flex: week.days }} title={`${formatDate(week.start)} – ${formatDate(week.end)}`}>{week.label}</span>)}</div>
            {todayVisible && <i className={styles.inlineTimelineTodayLabel}>Today</i>}
          </div>
          {rows.map((row) => <div className={styles.inlineTimelineMiddleRow} key={row.key}>{row.lane}</div>)}
        </div>
      </div>
      <div className={styles.inlineTimelineFixedRight}>
        <b className={styles.inlineTimelineFixedHeader}>%</b>
        {rows.map((row) => <div className={styles.inlineTimelineFixedRow} key={row.key}>{row.percent}</div>)}
      </div>
    </div>
    <footer className={styles.inlineTimelineLegend}>
      <span className={styles.inlineLegendPlanned}><i />Planned range</span><span className={styles.inlineLegendUtilised}><i />Actual (phase color)</span><span className={styles.inlineLegendExceeded}><i />Outside plan</span><span className={styles.inlineLegendWeekend}><i />Weekend activity</span>{todayVisible && <span className={styles.inlineLegendToday}><i />Today</span>}<span className={styles.inlineLegendUnscheduled}><i />No scheduled dates</span><em>Hover a phase bar for details</em>
    </footer>
    {tooltip && createPortal(<div className={styles.inlineTimelineTooltip} style={tooltip.style}>{tooltip.content}</div>, document.body)}
  </section>;
}

function TimelineHeader({ onBack, detail, title }: {
  onBack: () => void;
  detail?: string;
  title: string;
}) {
  return <header className={styles.inlineTimelineHeader}>
    <button type="button" onClick={onBack}><ChevronLeft />Back</button>
    <span />
    <CalendarDays />
    <strong>{title}</strong>
    <em className={styles.inlineTimelineContext}>Phase-wise breakdown</em>
    {detail && <small>{detail}</small>}
  </header>;
}

function buildTimelineRowParts({ phase, projectStart, projectEnd, totalDays, weeks, currency, todayVisible, laneStyle, onShowTooltip, onHideTooltip }: {
  phase: TimelinePhase;
  projectStart: Date;
  projectEnd: Date;
  totalDays: number;
  weeks: TimelineWeek[];
  currency: string;
  todayVisible: boolean;
  laneStyle: CSSProperties;
  onShowTooltip: (content: ReactNode, anchor: HTMLElement) => void;
  onHideTooltip: () => void;
}): TimelineRowParts {
  const utilised = Number(phase.utilised ?? 0);
  const hourless = !phase.hasHourPlan;
  const projectAligned = phase.planningMode === "PROJECT_ALIGNED";
  const hasPositiveHourPlan = phase.hasHourPlan && phase.planned > 0;
  const usage = hasPositiveHourPlan ? (utilised / phase.planned) * 100 : null;
  const rawStart = parseDate(phase.start);
  const rawEnd = parseDate(phase.end);
  const scheduled = Boolean(rawStart && rawEnd && rawEnd >= rawStart);
  const start = scheduled ? new Date(Math.max(rawStart!.getTime(), projectStart.getTime())) : null;
  const end = scheduled ? new Date(Math.min(rawEnd!.getTime(), projectEnd.getTime())) : null;
  const hasSpan = Boolean(start && end && end >= start);
  const left = hasSpan ? clamp(((start!.getTime() - projectStart.getTime()) / day / totalDays) * 100, 0, 100) : 0;
  const width = hasSpan ? clamp((((end!.getTime() - start!.getTime()) / day + 1) / totalDays) * 100, 1.5, 100 - left) : 0;
  const rawActualStart = parseDate(phase.actualStart);
  const rawActualEnd = parseDate(phase.actualEnd);
  const actualRangeValid = Boolean(rawActualStart && rawActualEnd && rawActualEnd >= rawActualStart);
  const visibleActualStart = actualRangeValid
    ? new Date(Math.max(rawActualStart!.getTime(), projectStart.getTime()))
    : null;
  const visibleActualEnd = actualRangeValid
    ? new Date(Math.min(rawActualEnd!.getTime(), projectEnd.getTime()))
    : null;
  const hasVisibleActual = Boolean(visibleActualStart && visibleActualEnd && visibleActualEnd >= visibleActualStart);
  const position = (rangeStart: Date | null, rangeEnd: Date | null) => {
    if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) return null;
    const segmentLeft = clamp(((rangeStart.getTime() - projectStart.getTime()) / day / totalDays) * 100, 0, 100);
    const segmentWidth = clamp((((rangeEnd.getTime() - rangeStart.getTime()) / day + 1) / totalDays) * 100, 0.25, 100 - segmentLeft);
    return { left: segmentLeft, width: segmentWidth };
  };
  const beforePlan = hasSpan && hasVisibleActual && visibleActualStart! < start!
    ? position(visibleActualStart, new Date(Math.min(visibleActualEnd!.getTime(), start!.getTime() - day)))
    : null;
  const withinPlan = hasSpan && hasVisibleActual
    ? position(
      new Date(Math.max(visibleActualStart!.getTime(), start!.getTime())),
      new Date(Math.min(visibleActualEnd!.getTime(), end!.getTime())),
    )
    : null;
  const afterPlan = hasSpan && hasVisibleActual && visibleActualEnd! > end!
    ? position(new Date(Math.max(visibleActualStart!.getTime(), end!.getTime() + day)), visibleActualEnd)
    : null;
  const usageLabelPosition = withinPlan ?? beforePlan ?? afterPlan;
  const rangeExceeded = Boolean(
    scheduled
    && actualRangeValid
    && (rawActualStart! < rawStart! || rawActualEnd! > rawEnd!),
  );
  const isOver = (hasPositiveHourPlan && Number(usage) > 100) || rangeExceeded;
  const status = hourless ? projectAligned ? "Project-aligned" : "Open-ended" : isOver ? "Over" : Number(usage) >= 80 ? "Watch" : hasSpan ? "Active" : "Pending";
  const tone = hourless ? "openEnded" : isOver ? "danger" : Number(usage) >= 80 ? "watch" : hasSpan ? "active" : "pending";
  const alertFill = "#dc403b";
  const fill = phase.color;
  const metricColor = hasPositiveHourPlan && Number(usage) > 100 ? alertFill : phase.color;
  const margin = phase.commercial - phase.internal;
  const plannedDurationDays = scheduled ? Math.floor((rawEnd!.getTime() - rawStart!.getTime()) / day) + 1 : 0;
  const actualDurationDays = actualRangeValid ? Math.floor((rawActualEnd!.getTime() - rawActualStart!.getTime()) / day) + 1 : 0;
  const timelineCompletion = plannedDurationDays > 0 ? (actualDurationDays / plannedDurationDays) * 100 : 0;
  const timelineExceeded = timelineCompletion > 100;
  const currentDate = new Date();
  const todayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  const daysSinceLastTimesheet = rawActualEnd ? Math.floor((todayDate.getTime() - rawActualEnd.getTime()) / day) : null;
  const actualEndDisplay = rawActualEnd
    ? daysSinceLastTimesheet != null && daysSinceLastTimesheet >= 0 && daysSinceLastTimesheet <= 2 ? "Ongoing" : formatDate(rawActualEnd)
    : "—";

  const weeklyActivity = weeks.map((week) => {
    const entries = phase.dailyActivity.filter((entry) => {
      const activityDate = parseDate(entry.activityDate);
      return activityDate && activityDate >= week.start && activityDate <= week.end;
    });
    return {
      ...week,
      weekdayHours: entries.filter((entry) => !entry.weekend).reduce((sum, entry) => sum + Number(entry.hours ?? 0), 0),
      weekendHours: entries.filter((entry) => entry.weekend).reduce((sum, entry) => sum + Number(entry.hours ?? 0), 0),
    };
  });
  const activitySegments = weeklyActivity.reduce<Array<{ key: string; start: Date; end: Date; color: string }>>((segments, week) => {
    if (week.weekdayHours + week.weekendHours <= 0) return segments;
    const outsidePlan = scheduled && (week.end < rawStart! || week.start > rawEnd!);
    const color = outsidePlan ? alertFill : fill;
    const previous = segments.at(-1);
    if (previous && previous.color === color && addDays(previous.end, 1).getTime() === week.start.getTime()) {
      previous.end = week.end;
      return segments;
    }
    segments.push({ key: week.key, start: week.start, end: week.end, color });
    return segments;
  }, []).map((segment) => ({ ...segment, marker: position(segment.start, segment.end) })).filter((segment) => segment.marker != null);
  const segmentLabelPosition = activitySegments[0]?.marker ?? usageLabelPosition;

  const tooltipContent = <>
    <strong><span><i style={{ background: phase.color }} />{phase.name}</span><b style={{ color: timelineExceeded ? "#ff7777" : phase.color }}>{number.format(timelineCompletion)}%</b></strong>
    <div className={styles.inlineTimelineTooltipSection}>
      <span>Start (Planned)<b>{rawStart ? formatDate(rawStart) : "—"}</b></span>
      <span>End (Planned)<b>{rawEnd ? formatDate(rawEnd) : "—"}</b></span>
      <span>Actual Start (First TS)<b>{rawActualStart ? formatDate(rawActualStart) : "—"}</b></span>
      <span>Actual End (Last TS)<b className={actualEndDisplay === "Ongoing" ? styles.inlineTimelineOngoing : undefined}>{actualEndDisplay}</b></span>
    </div>
    <hr />
    <div className={styles.inlineTimelineTooltipSection}>
      <span>Hours Allocated<b>{hourless ? "N/A" : hours(phase.planned)}</b></span>
      <span>Hours Utilized<b>{hours(utilised)}</b></span>
      <span>Utilization %<b style={{ color: metricColor }}>{hourless || usage == null ? "N/A" : `${number.format(usage)}%`}</b></span>
    </div>
    <hr />
    <div className={styles.inlineTimelineTooltipSection}>
      <span>Commercial Burn<b>{formatCurrency(phase.commercial, currency, 0)}</b></span>
      <span>Internal Cost<b>{formatCurrency(phase.internal, currency, 0)}</b></span>
      <span>Margin<b className={margin >= 0 ? styles.inlineTimelineMarginPositive : styles.inlineTimelineMarginNegative}>{formatCurrency(margin, currency, 0)}</b></span>
    </div>
  </>;

  return {
    key: phase.key,
    phase: <div className={styles.inlineTimelinePhase}>
      <i style={{ background: phase.color }} />
      <span><strong title={phase.name}>{phase.name}</strong><small>{hourless ? `${hours(utilised)} used` : `${hours(utilised)} / ${hours(phase.planned)}`}</small></span>
      <b className={styles[`inlineTimeline${tone}`]}>{status}</b>
    </div>,
    lane: <div className={styles.inlineTimelineLane} style={laneStyle}>
      <div className={styles.inlineTimelineGuides}>{weeks.map((week) => <i key={week.key} style={{ flex: week.days }} />)}</div>
      {todayVisible && <span className={styles.inlineTimelineToday} />}
      {hasSpan ? <div className={styles.inlineTimelineSpan} style={{ left: `${left}%`, width: `${width}%`, background: `${phase.color}19`  }} tabIndex={0}
        onMouseEnter={(event) => onShowTooltip(tooltipContent, event.currentTarget)}
        onMouseLeave={onHideTooltip}
        onFocus={(event) => onShowTooltip(tooltipContent, event.currentTarget)}
        onBlur={onHideTooltip}>
        <small>{formatDate(start!)}</small><small>{formatDate(end!)}</small>
      </div> : <div className={styles.inlineTimelineNoDates} style={{ borderColor: `${phase.color}55`, color: phase.color }}>No scheduled dates</div>}
      {weeklyActivity.filter((week) => week.weekendHours > 0).map((week) => {
        const marker = position(week.start, week.end);
        if (!marker) return null;
        return <span
          key={`weekend-${week.key}`}
          className={styles.inlineTimelineWeekendActivity}
          style={{ left: `${marker.left}%`, width: `${marker.width}%` }}
          aria-hidden="true"
        />;
      })}
      {phase.dailyActivity.length > 0
        ? activitySegments.map((segment) => <span key={segment.key} className={styles.inlineTimelineFill} style={{ left: `${segment.marker!.left}%`, width: `${segment.marker!.width}%`, background: segment.color }} />)
        : <>{beforePlan && <span className={styles.inlineTimelineFill} style={{ left: `${beforePlan.left}%`, width: `${beforePlan.width}%`, background: alertFill }} />}{withinPlan && <span className={styles.inlineTimelineFill} style={{ left: `${withinPlan.left}%`, width: `${withinPlan.width}%`, background: fill }} />}{afterPlan && <span className={styles.inlineTimelineFill} style={{ left: `${afterPlan.left}%`, width: `${afterPlan.width}%`, background: alertFill }} />}</>}
      {hasSpan && segmentLabelPosition && <b className={styles.inlineTimelineUsageLabel} style={{ left: `${segmentLabelPosition.left}%` }}>{number.format(timelineCompletion)}%</b>}
    </div>,
    percent: <strong className={styles.inlineTimelinePercent} style={{ color: scheduled ? (timelineExceeded ? alertFill : phase.color) : undefined }}>{scheduled ? `${number.format(timelineCompletion)}%` : hourless ? "N/A" : "—"}</strong>,
  };
}

function buildMonths(start: Date, end: Date) {
  const months: Array<{ key: string; label: string; days: number }> = [];
  let cursor = new Date(start);
  while (cursor <= end) {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const segmentEnd = monthEnd < end ? monthEnd : end;
    months.push({ key: `${cursor.getFullYear()}-${cursor.getMonth()}`, label: monthLabel(cursor), days: Math.floor((segmentEnd.getTime() - cursor.getTime()) / day) + 1 });
    cursor = new Date(segmentEnd.getTime() + day);
  }
  return months;
}

function buildWeeks(start: Date, end: Date): TimelineWeek[] {
  const weeks: TimelineWeek[] = [];
  let cursor = new Date(start);
  let month = -1;
  let weekNumber = 0;
  while (cursor <= end) {
    const daysUntilSunday = (7 - cursor.getDay()) % 7;
    const segmentEnd = new Date(Math.min(addDays(cursor, daysUntilSunday).getTime(), end.getTime()));
    const labelDate = addDays(cursor, Math.floor((segmentEnd.getTime() - cursor.getTime()) / day / 2));
    if (labelDate.getMonth() !== month) {
      month = labelDate.getMonth();
      weekNumber = 1;
    } else {
      weekNumber += 1;
    }
    weeks.push({
      key: `${dateKey(cursor)}-${dateKey(segmentEnd)}`,
      start: new Date(cursor),
      end: segmentEnd,
      days: Math.floor((segmentEnd.getTime() - cursor.getTime()) / day) + 1,
      label: `W${weekNumber}`,
    });
    cursor = addDays(segmentEnd, 1);
  }
  return weeks;
}
